/**
 * useNotifications — Composable for the Sensando notification engine.
 *
 * Three severity tiers:
 *   CRITICAL → Full-screen banner with alarm sound. Cannot dismiss without action.
 *   WARNING  → Amber toast (top-right, 10s auto-dismiss, soft chime).
 *   INFO     → Badge on nav item only. No interruption.
 *
 * Subscribes to socket events and dispatches visual + audio notifications
 * based on the agent's notification config.
 *
 * Usage:
 *   const { criticalAlerts, toasts, dismissToast, acknowledge } = useNotifications()
 */

import { ref, computed, reactive, onMounted, onUnmounted, watch } from 'vue'
import { onEvent, Events } from '@/services/SocketService'

// ─── Audio ────────────────────────────────────────────────────
let audioContext = null
const SOUNDS = {
  alarm: null,
  chime: null,
}

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)()
  }
  return audioContext
}

/**
 * Generate a synthetic alarm tone (no audio files needed).
 * @param {'alarm'|'chime'} type
 */
function playSound(type) {
  try {
    const ctx = getAudioContext()
    if (ctx.state === 'suspended') ctx.resume()

    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.connect(gain)
    gain.connect(ctx.destination)

    if (type === 'alarm') {
      // Urgent alternating tone
      oscillator.type = 'square'
      oscillator.frequency.setValueAtTime(880, ctx.currentTime)
      oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.2)
      oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.4)
      oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.6)
      gain.gain.setValueAtTime(0.15, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8)
      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + 0.8)
    } else {
      // Soft notification chime
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(523, ctx.currentTime) // C5
      oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.15) // E5
      gain.gain.setValueAtTime(0.08, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + 0.4)
    }
  } catch (e) {
    // Audio not available — silently ignore
    console.warn('[Notifications] Audio playback failed:', e.message)
  }
}

// ─── Notification state ───────────────────────────────────────
const MAX_TOASTS = 5
const TOAST_DURATION_MS = 10000
const MAX_CRITICAL = 10

/**
 * Create the notification engine composable.
 * Call once in App.vue or a layout component to start listening.
 */
export function useNotifications() {
  /** Active critical alerts requiring acknowledgement */
  const criticalAlerts = ref([])

  /** Active toast notifications (auto-dismiss) */
  const toasts = ref([])

  /** Badge counter for nav items (per agent) */
  const badges = reactive({
    centinela: 0,
    jefe: 0,
    supervisor: 0,
    system: 0,
  })

  /** Global mute toggle */
  const muted = ref(false)

  /** Whether the critical overlay is showing */
  const showCriticalOverlay = computed(() => criticalAlerts.value.length > 0)

  /** Total unread badge count */
  const totalBadge = computed(() =>
    Object.values(badges).reduce((sum, n) => sum + n, 0),
  )

  // ─── Internal helpers ────────────────────────────────────
  let toastIdCounter = 0

  function resolveAgent(event) {
    if (event.agent) return event.agent
    const type = (event.type || '').toLowerCase()
    if (type.includes('security') || type.includes('centinela') || type.includes('camera'))
      return 'centinela'
    if (type.includes('fleet') || type.includes('jefe') || type.includes('mission') || type.includes('driver'))
      return 'jefe'
    if (type.includes('process') || type.includes('supervisor') || type.includes('ppe') || type.includes('line'))
      return 'supervisor'
    return 'system'
  }

  function resolveSeverity(event) {
    if (event.severity) return event.severity
    const type = (event.type || '').toLowerCase()
    if (type.includes('critical') || type.includes('sos') || type.includes('crash'))
      return 'critical'
    if (type.includes('alert') || type.includes('breach') || type.includes('speeding'))
      return 'high'
    if (type.includes('warning') || type.includes('idle'))
      return 'medium'
    return 'info'
  }

  /**
   * Process an incoming event and dispatch the appropriate notification.
   */
  function processEvent(rawEvent) {
    const agent = resolveAgent(rawEvent)
    const severity = resolveSeverity(rawEvent)

    const notification = {
      id: rawEvent.id || `notif_${Date.now()}_${++toastIdCounter}`,
      agent,
      severity,
      title: rawEvent.title || rawEvent.message || 'Event',
      narrative: rawEvent.narrative || rawEvent.description || '',
      source: rawEvent.source || rawEvent.asset_id || null,
      timestamp: rawEvent.timestamp || new Date().toISOString(),
      data: rawEvent,
    }

    // Always increment badge
    if (badges[agent] !== undefined) {
      badges[agent]++
    } else {
      badges.system++
    }

    // Route by severity
    if (severity === 'critical') {
      dispatchCritical(notification)
    } else if (['high', 'medium'].includes(severity)) {
      dispatchToast(notification)
    }
    // INFO severity => badge only, no visual interruption
  }

  /**
   * Dispatch a CRITICAL notification — full-screen banner + alarm.
   */
  function dispatchCritical(notification) {
    criticalAlerts.value.unshift(notification)
    if (criticalAlerts.value.length > MAX_CRITICAL) {
      criticalAlerts.value = criticalAlerts.value.slice(0, MAX_CRITICAL)
    }

    // Play alarm sound
    if (!muted.value) {
      playSound('alarm')
    }
  }

  /**
   * Dispatch a WARNING/HIGH toast — auto-dismiss after 10s + chime.
   */
  function dispatchToast(notification) {
    toasts.value.unshift(notification)
    if (toasts.value.length > MAX_TOASTS) {
      toasts.value = toasts.value.slice(0, MAX_TOASTS)
    }

    // Play chime sound
    if (!muted.value && notification.severity === 'high') {
      playSound('chime')
    }

    // Auto-dismiss
    setTimeout(() => {
      dismissToast(notification.id)
    }, TOAST_DURATION_MS)
  }

  /**
   * Dismiss a toast notification.
   */
  function dismissToast(id) {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }

  /**
   * Acknowledge a critical alert (removes from overlay).
   * The user MUST pick an action (Acknowledge or Escalate) — cannot just close.
   * @param {String} id — Notification ID
   * @param {'acknowledge'|'escalate'} action — Required action
   */
  function acknowledge(id, action = 'acknowledge') {
    criticalAlerts.value = criticalAlerts.value.filter(a => a.id !== id)
    return { id, action, timestamp: new Date().toISOString() }
  }

  /**
   * Clear badge count for an agent.
   */
  function clearBadge(agentKey) {
    if (badges[agentKey] !== undefined) {
      badges[agentKey] = 0
    }
  }

  /**
   * Clear all badges.
   */
  function clearAllBadges() {
    Object.keys(badges).forEach(k => { badges[k] = 0 })
  }

  /**
   * Toggle global mute.
   */
  function toggleMute() {
    muted.value = !muted.value
  }

  // ─── Socket subscriptions ────────────────────────────────
  const unsubs = []

  function startListening() {
    // Listen to device events
    unsubs.push(
      onEvent(Events.DEVICE_EVENT, processEvent),
    )
    unsubs.push(
      onEvent(Events.NARRATIVE_EVENT, (event) => {
        // Only process narrative events with severity >= medium
        const sev = resolveSeverity(event)
        if (['critical', 'high', 'medium'].includes(sev)) {
          processEvent(event)
        }
      }),
    )
    unsubs.push(
      onEvent(Events.AGENT_STATUS, (event) => {
        // Agent going down is a warning
        if (event.status === 'down' || event.status === 'degraded') {
          processEvent({
            ...event,
            severity: event.status === 'down' ? 'critical' : 'medium',
            title: `Agent ${event.agent || 'unknown'} is ${event.status}`,
          })
        }
      }),
    )
    unsubs.push(
      onEvent(Events.GEOFENCE_BREACH, processEvent),
    )
  }

  function stopListening() {
    unsubs.forEach(fn => fn())
    unsubs.length = 0
  }

  // ─── Lifecycle ───────────────────────────────────────────
  onMounted(startListening)
  onUnmounted(stopListening)

  return {
    // State
    criticalAlerts,
    toasts,
    badges,
    muted,
    showCriticalOverlay,
    totalBadge,

    // Actions
    processEvent,
    dismissToast,
    acknowledge,
    clearBadge,
    clearAllBadges,
    toggleMute,
    startListening,
    stopListening,
  }
}
